import { OfficeMember } from '@/components/OfficeMember';
import { useGetPopupContainer } from '@/components/ReportForm/Container/PopupContainer';
import {
  InputBaseType,
  SelectType,
} from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import { FormSelectProps } from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { PAYEE_FORM_MAX_LENGTH } from '../constants';

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues>,
  'label' | 'tooltip' | 'caption' | 'hidden'
> &
  SelectType & {
    maxLength?: number;
  };

const InnerApPayeeFormInputOfficeMemberSelect = <
  TFieldValues extends FieldValues
>({
  control,
  name,
  inputId,
  defaultOptions,
  required = false,
  maxLength = PAYEE_FORM_MAX_LENGTH.OFFICE_MEMBERS,
}: Props<TFieldValues>) => {
  const containerRef = useGetPopupContainer();
  const { t } = useTranslation();

  const selectProps: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'mode'
  > = useMemo(() => {
    return {
      getPopupContainer: containerRef,
      placeholder: t('ap_payee_contact_office_member_names_placeholder'),
    };
  }, [containerRef, t]);

  const rules = useMemo(
    () => ({
      required: required ? t('required') : false,
      validate: {
        maxLength: (value: string[] | undefined) => {
          if (value && value.length > maxLength) {
            return t('ap_payee_contact_office_member_names_max_error');
          }
          return true;
        },
      },
    }),
    [required, t, maxLength]
  );

  return (
    <OfficeMember<TFieldValues>
      control={control}
      name={name}
      inputId={inputId}
      selectProps={selectProps}
      defaultOptions={defaultOptions}
      rules={rules}
    />
  );
};

InnerApPayeeFormInputOfficeMemberSelect.displayName =
  'InnerApPayeeFormInputOfficeMemberSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApPayeeFormInputOfficeMemberSelect = memo((props) => (
  <InnerApPayeeFormInputOfficeMemberSelect {...props} />
)) as typeof InnerApPayeeFormInputOfficeMemberSelect;
ApPayeeFormInputOfficeMemberSelect.displayName =
  'ApPayeeFormInputOfficeMemberSelect';
